import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Form, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import GooglePayButton from "@google-pay/button-react";
import { PBLResponseContext, useUuidPblData } from "../../UUIDContexted";
import { useTheme } from "../../context/ThemeContext.tsx";
import { DisplayItem, PaymentAuthorizationResult, productTypeMap, TransactionInfo } from "./GooglePay.types.ts";
import { calculateNewTransactionInfo, getShippingOptionsForMunich } from "./GooglePay.helper.ts";
import { Item } from "../../types/api.types.ts";
import PaylaDcs from "../PaylaDsc";
import { v4 as uuidv4 } from "uuid";
import PaymentNotSupported from "../PaymentNotSupported.tsx";
import CONSTANTS from "../../utils/constants.js";

const GooglePay: React.FC = () => {
  const pblData = useUuidPblData();
  const { theme } = useTheme();

  const { setPblResponseData } = useContext(PBLResponseContext);

  const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsError, setTermsError] = useState("");

  const snippetToken = useRef<string>("");

  const intl = useIntl();
  const buttonLocale = intl.locale.includes("en") ? "en" : intl.locale.includes("de") ? "de" : "en";
  const buttonColor = theme === "light" ? "white" : "black";
  const environment = window.location.origin === CONSTANTS.PROD_URL ? "PRODUCTION" : "TEST";

  const merchantInfo = {
    merchantId: "12345678901234567890",
    merchantName: "Demo Merchant",
  };

  const baseGooglePayRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: "CARD",
        parameters: {
          allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
          allowedCardNetworks: ["MASTERCARD", "VISA"],
          allowedIssuerCountryCodes: ["DE", "US"],
          billingAddressRequired: true,
          billingAddressParameters: {
            format: "FULL",
            phoneNumberRequired: false,
          },
        },
        tokenizationSpecification: {
          type: "PAYMENT_GATEWAY",
          parameters: {
            gateway: "payonegmbh",
            gatewayMerchantId: "XSYznIYWmi",
          },
        },
      },
    ],
    merchantInfo,
  } as any;

  Object.freeze(baseGooglePayRequest);

  function convertToDisplayItem(items: Item[]): DisplayItem[] {
    const displayItems: DisplayItem[] = items.map((item) => ({
      label: item.invoiceData.description,
      type: productTypeMap[item.orderLineDetails.productType],
      price: ((item.orderLineDetails.productPrice * item.orderLineDetails.quantity) / 100).toFixed(2),
    }));

    return displayItems;
  }

  const transactionInfo = {
    totalPriceStatus: "ESTIMATED",
    totalPriceLabel: "Total",
    totalPrice: (pblData.amountOfMoney.amount / 100).toFixed(2),
    currencyCode: pblData.amountOfMoney.currencyCode,
    countryCode: "DE",
    displayItems: convertToDisplayItem(pblData.items),
  } satisfies TransactionInfo;

  const callbackIntents = {
    callbackIntents: ["PAYMENT_AUTHORIZATION", "SHIPPING_ADDRESS", "SHIPPING_OPTION"],
    emailRequired: true,
    shippingAddressRequired: true,
    shippingAddressParameters: {
      allowedCountryCodes: ["DE"],
      phoneNumberRequired: true,
      format: "FULL",
    },
    shippingOptionRequired: true,
    shippingOptionParameters: {
      defaultSelectedOptionId: "shipping-001",
      shippingOptions: [
        {
          id: "shipping-001",
          label: "€0.00: Free shipping",
          description: "Free Shipping delivered in 5 business days.",
        },
        {
          id: "shipping-002",
          label: "€1.99: Standard shipping",
          description: "Standard shipping delivered in 3 business days.",
        },
        {
          id: "shipping-003",
          label: "€9.99: Express shipping",
          description: "Express shipping delivered in 1 business day.",
        },
      ],
    },
  };

  useEffect(() => {
    if (pblData && pblData.paymentMethods && pblData.paymentMethods.length > 0) {
      const isPaymentMethodAvailable = pblData.paymentMethods.includes("320");
      setIsPaymentMethodAvailable(isPaymentMethodAvailable);
    }
  }, [pblData]);

  useEffect(() => {
    const partnerMerchantId = pblData ? pblData.bnplId : "";
    snippetToken.current = `${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`;
    setPblResponseData(null);
  }, []);

  const processPayment = async (_paymentData: any) => {
    // passing paymentData.paymentMethodData.tokenizationData.token
    // to payment gateway (commerce portal) to process payment
    // and throw appropriate error
    return null;
  };

  const onLoadPaymentData = (paymentData: google.payments.api.PaymentData) => {
    console.log(paymentData);
  };

  const onPaymentAuthorized = async (paymentData: any) => {
    try {
      await processPayment(paymentData);
      return { transactionState: "SUCCESS" } as PaymentAuthorizationResult;
    } catch {
      return {
        transactionState: "ERROR",
        error: {
          intent: "PAYMENT_AUTHORIZATION",
          message: "Insufficient funds",
          reason: "PAYMENT_DATA_INVALID",
        },
      } as PaymentAuthorizationResult;
    }
  };

  function onPaymentDataChanged(intermediatePaymentData: google.payments.api.IntermediatePaymentData) {
    let paymentDataRequestUpdate: google.payments.api.PaymentDataRequestUpdate = {};
    let shippingAddress = intermediatePaymentData.shippingAddress;
    let shippingOptionData = intermediatePaymentData.shippingOptionData;

    // console.log("onPaymentDataChanged", intermediatePaymentData);

    switch (intermediatePaymentData.callbackTrigger) {
      case "INITIALIZE":
      case "SHIPPING_ADDRESS":
        // Read intermediatePaymentData.transactionInfo
        // Read intermediatePaymentData.shippingAddress
        // Update paymentDataRequestUpdate.newTransactionInfo

        // For example, read the shipping address and update the shipping options
        // if the postal code belongs to Munich
        const postalCode = Number(shippingAddress?.postalCode);
        if (postalCode >= 80331 && postalCode <= 81929) {
          const newShippingOptionParameters = getShippingOptionsForMunich();
          const newTransactionInfo = calculateNewTransactionInfo(
            newShippingOptionParameters.defaultSelectedOptionId,
            transactionInfo,
          );
          paymentDataRequestUpdate.newShippingOptionParameters = newShippingOptionParameters;
          paymentDataRequestUpdate.newTransactionInfo = newTransactionInfo;
        } else if (postalCode === 18581) {
          // throw error is postal code is 18581
          paymentDataRequestUpdate.error = {
            reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
            message: "Cannot ship to the selected address",
            intent: "SHIPPING_ADDRESS",
          };
        }
        break;
      case "SHIPPING_OPTION":
        // Read intermediatePaymentData.transactionInfo
        // Read intermediatePaymentData.shippingOptionData
        // Update paymentDataRequestUpdate.newTransactionInfo
        // Update paymentDataRequestUpdate.newShippingOptionParameters

        const newTransactionInfo = calculateNewTransactionInfo(shippingOptionData?.id ?? "", transactionInfo);
        paymentDataRequestUpdate.newTransactionInfo = newTransactionInfo;
        break;
      default:
      // Update paymentDataRequestUpdate.error
    }

    return paymentDataRequestUpdate;
  }

  const handleClick = (e: Event) => {
    if (!termsAgreed) {
      e.preventDefault();
      setTermsError(CONSTANTS.VALIDATION_ERRORS.TERMS_AND_CONDITIONS.NOT_AGREED);
      return;
    }
  };

  if (!isPaymentMethodAvailable) {
    return <PaymentNotSupported />;
  }

  return (
    <Container className="gpay-wrapper">
      <Row className="d-flex justify-content-center align-items-end mb-24 mt-2">
        <h5 data-testid="pm-label-terms-payment" className="section-title">
          {intl.formatMessage({
            id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE,
            defaultMessage: "Terms of Payment",
          })}
        </h5>
        <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
          <Form.Check.Input
            type="checkbox"
            className="terms-checkbox-input m-0"
            checked={termsAgreed}
            onChange={(e) => {
              setTermsAgreed(e.target.checked);
              setTermsError("");
            }}
            data-testid="pm-terms-checkbox-input"
          />
          <label className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
            <FormattedMessage
              id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
              defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
            />
          </label>
        </div>
        {!termsAgreed && termsError !== "" && (
          <span id="not-agree" className="error_message" data-testid="pm-not-agree">
            <FormattedMessage
              id={CONSTANTS.VALIDATION_ERRORS.TERMS_AND_CONDITIONS.NOT_AGREED}
              defaultMessage="Please agree to our terms and conditions before submiting"
            />
          </span>
        )}
      </Row>
      <Row>
        <GooglePayButton
          className="mt-2"
          style={{ width: "100%" }}
          environment={environment}
          buttonType="pay"
          buttonColor={buttonColor}
          buttonLocale={buttonLocale}
          buttonSizeMode="fill"
          paymentRequest={{
            ...baseGooglePayRequest,
            transactionInfo: transactionInfo,
            ...callbackIntents,
          }}
          onLoadPaymentData={onLoadPaymentData}
          onClick={handleClick}
          onPaymentAuthorized={onPaymentAuthorized}
          onPaymentDataChanged={onPaymentDataChanged}
        />
      </Row>
      <PaylaDcs snippetToken={snippetToken.current} agreed={termsAgreed} />
    </Container>
  );
};

export default GooglePay;
